import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ChevronDown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useIOSPortrait } from "@/hooks/useIOSPortrait";

export function HeroSection() {
  const isIOSPortrait = useIOSPortrait();

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: "smooth"
    });
  };

  // iOS Portrait: Render completely static version to prevent Framer Motion transforms
  if (isIOSPortrait) {
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video autoPlay muted loop playsInline preload="auto" className="w-full h-full object-cover">
            <source src="/videos/hero-video.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Content - Static */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            {/* Decorative Element */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <Sparkles className="h-5 w-5 text-gold" />
              <span className="text-sm font-medium tracking-widest uppercase text-foreground/80 drop-shadow-sm">
                Where Luxury Meets Radiance
              </span>
              <Sparkles className="h-5 w-5 text-gold" />
            </div>

            {/* Main Heading */}
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-foreground mb-6 leading-tight drop-shadow-sm">
              Gentle Skincare.
              <br />
              <span className="text-gradient">Real Results.</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto mb-10 drop-shadow-sm sm:text-[sidebar-primary-foreground]">
              Curated skincare products you can trust. Authentic formulas from 
              trusted global brands, selected with care for your unique skin journey.
            </p>

            {/* CTA Buttons */}
            <div className="flex items-center justify-center mb-20">
              <Button variant="outline" size="xl" className="bg-card/80" asChild>
                <Link to="/products">View Products</Link>
              </Button>
            </div>
          </div>

          {/* Scroll Indicator - Static */}
          <button
            onClick={scrollToContent}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-foreground/70 hover:text-primary transition-colors cursor-pointer"
          >
            <span className="text-xs font-medium tracking-wider uppercase drop-shadow-sm">Discover More</span>
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
          <video autoPlay muted loop playsInline preload="auto" className="w-full h-full object-cover">
            <source src="/videos/hero-video.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl mx-auto"
        >
          {/* Decorative Element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="flex items-center justify-center gap-2 mb-6"
          >
            <Sparkles className="h-5 w-5 text-gold" />
            <span className="text-sm font-medium tracking-widest uppercase text-foreground/80 drop-shadow-sm">
              Where Luxury Meets Radiance
            </span>
            <Sparkles className="h-5 w-5 text-gold" />
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-foreground mb-6 leading-tight drop-shadow-sm"
          >
            Gentle Skincare.
            <br />
            <span className="text-gradient">Real Results.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-lg text-foreground/80 max-w-2xl mx-auto mb-10 drop-shadow-sm sm:text-[sidebar-primary-foreground]"
          >
            Curated skincare products you can trust. Authentic formulas from 
            trusted global brands, selected with care for your unique skin journey.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="flex items-center justify-center mb-20"
          >
            <Button variant="outline" size="xl" className="bg-card/80" asChild>
              <Link to="/products">View Products</Link>
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.button
          onClick={scrollToContent}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-foreground/70 hover:text-primary transition-colors cursor-pointer"
        >
          <span className="text-xs font-medium tracking-wider uppercase drop-shadow-sm">Discover More</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
          >
            <ChevronDown className="h-5 w-5" />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
}
